import Component from '@glimmer/component'
import { service } from '@ember/service'
import { eq } from 'ember-truth-helpers'
import { t, type IntlService } from 'ember-intl'
import { pdfDownloadEvent } from 'teacher-ui-client/modifiers/pdf-download-event'
import type { ThemeKeys } from 'teacher-ui-client/utils/themes'
import type { PDFType } from 'teacher-ui-client/components/printables/pdf-types'
import miniBook from '@images/printables/mini-book.png'
import doubleSided from '@images/printables/double-sided.png'
import singleSided from '@images/printables/single-sided.png'
import miniBookColor from '@images/printables/mini-book-colour.png'
import doubleSidedColor from '@images/printables/double-sided-colour.png'
import singleSidedColor from '@images/printables/single-sided-colour.png'
import type { PrintablesLibraryBookEntry } from 'teacher-ui-client/contentful-client/entry-types'

interface Signature {
  Element: HTMLAnchorElement
  Args: {
    pdfType: PDFType
    printable: Exclude<PrintablesLibraryBookEntry['fields'][PDFType], undefined>
    bookCode: string
    role: string
    themeKey?: ThemeKeys
  }
}

const pdfTypesAndImages: Record<PDFType, string> = {
  singleSidedPdf: singleSidedColor,
  doubleSidedPdf: doubleSidedColor,
  miniBookPdf: miniBookColor,
  // eslint-disable-next-line customPlugin/prefer-american-color
  colourPdf: singleSidedColor,
  blackAndWhitePdf: singleSided,
  singleBwPdf: singleSided,
  doubleBwPdf: doubleSided,
  miniBookBwPdf: miniBook,
}

export class ResourcesPrintablesPDF extends Component<Signature> {
  @service intl!: IntlService

  get printableViewModel() {
    const { pdfType, printable } = this.args
    return {
      title: printable.fields?.title,
      fileName: printable.fields?.file?.fileName,
      url: printable.fields?.file?.url,
      pdfType: pdfType,
      pdfTypeFormatted: this.intl.t(`printables.mediaType.${pdfType}`),
      pdfTypeImgUrl: pdfTypesAndImages[pdfType],
    }
  }

  <template>
    <a
      href={{this.printableViewModel.url}}
      download={{this.printableViewModel.fileName}}
      target="_blank"
      rel="noreferrer noopener"
      class="border-dusty-black-100 text-dusty-black-300 flex w-28 flex-col rounded border-2 p-2 pb-3 shadow-md
        {{if (eq @themeKey 'blue') 'hover:border-oceany-blue-350'}}
        {{if (eq @themeKey 'green') 'hover:border-ms-green-350'}}
        {{if (eq @themeKey 'default') 'hover:border-dusty-black-300'}}
        "
      ...attributes
      {{pdfDownloadEvent
        category="Teacher Library - Printables"
        label=this.printableViewModel.title
        pdfType=this.printableViewModel.pdfType
        file=this.printableViewModel.fileName
        bookCode=@bookCode
        role=@role
      }}
    >
      <div class="text-center text-xs">{{t "printables.pdf"}}</div>
      <div class="mb-3 text-center text-xs">
        {{this.printableViewModel.pdfTypeFormatted}}
      </div>
      <img
        src={{this.printableViewModel.pdfTypeImgUrl}}
        alt={{this.printableViewModel.pdfTypeFormatted}}
        class="mx-auto mt-auto w-1/3"
      />
    </a>
  </template>
}

export default ResourcesPrintablesPDF
